import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { seriesWidgetEntrypointHeadlineContent$key } from "~/queries/__generated__/seriesWidgetEntrypointHeadlineContent.graphql";

import {
  Container,
  Headline,
  HeadlineContainer,
  HighlightText,
  Indicator,
  StyledEntityLink,
} from "./styles";

export type Props = {
  className?: string;
  reference: seriesWidgetEntrypointHeadlineContent$key;
};

export const SeriesWidgetEntrypointHeadline: FunctionComponent<Props> = ({
  reference: reference_,
}) => {
  const content = useFragment(
    graphql`
      fragment seriesWidgetEntrypointHeadlineContent on Article {
        entityId
        seriesContainer {
          ...entityLink
          entityId
          headline
          series {
            ... on Article {
              entityId
            }
          }
        }
      }
    `,
    reference_,
  );

  const campaign = useMemo(
    () => `${(content.seriesContainer?.headline ?? "").replaceAll(/\s+/g, "_")}`,
    [content.seriesContainer?.headline],
  );

  const total = useMemo(
    () => content.seriesContainer?.series?.length ?? 0,
    [content.seriesContainer?.series],
  );
  const currentIndex = useMemo(() => {
    if (total === 0) return 0;
    return (
      (content.seriesContainer?.series?.findIndex?.(
        series => series && series.entityId === content.entityId,
      ) ?? 0) + 1
    );
  }, [content.entityId, content.seriesContainer?.series, total]);

  return content.seriesContainer ? (
    <StyledEntityLink
      query={{
        campaign,
        module: "series_LHS_header",
        pgtype: "article",
      }}
      reference={content.seriesContainer}
    >
      <Container>
        <HeadlineContainer>
          <HighlightText>SCMP Series</HighlightText>
          <Headline>{content.seriesContainer?.headline ?? ""}</Headline>
        </HeadlineContainer>
        <Indicator>
          <strong>{currentIndex}</strong> of {total}
        </Indicator>
      </Container>
    </StyledEntityLink>
  ) : null;
};

SeriesWidgetEntrypointHeadline.displayName = "SeriesWidgetEntrypointHeadline";
